import {
  type AircraftBaseData,
  type AircraftData,
  type MaoCrewDuty,
} from '#entities';
import { cacheWrapper, HOUR_MS } from '#shared';

import { getAircraftCrewDutyRanges } from './getAircraftCrewDutyRanges';
import { getAircraftEvents } from './getAircraftEvents';
import { getAircraftLocationInfo } from './getAircraftLocationInfo';
import { getCrewDutyEvents } from './getCrewDutyEvents';

const _getAircraftInfo = async (
  aircraftBaseInfo: AircraftBaseData,
): Promise<AircraftData> => {
  const now = Date.now();
  const dutyRanges = getAircraftCrewDutyRanges(aircraftBaseInfo.CrewDuties);

  const aircraftEvents = getAircraftEvents(
    aircraftBaseInfo.Location,
    aircraftBaseInfo.Maintenances,
    aircraftBaseInfo.Trips,
  );

  const closestAirport = aircraftBaseInfo.closestAirport ?? null;

  const actualAircraftEvent = aircraftEvents.find(
    e => e.range[0] - 2 * HOUR_MS < now,
  );
  const nextTrip =
    aircraftBaseInfo.Trips.find(t => t.TripStartUnix > now) ?? null;

  const actualTrip =
    aircraftBaseInfo.Trips.find(
      t => t.TripStartUnix < now && t.TripEndUnix > now,
    ) ?? nextTrip;

  const prevTripLeg =
    actualTrip?.Legs.find(l => !!l.Movement?.IsOnBlocks) || null;

  const actualTripLeg =
    actualTrip?.Legs.find(l => !l.Movement?.IsOnBlocks) ?? null;

  const nextTripLeg =
    actualTrip?.Legs.find(
      l => !l.Movement?.IsOffBlocks && !l.Movement?.IsOnBlocks,
    ) || null;

  const departureAirport =
    actualTripLeg?.DepartureAirport ?? prevTripLeg?.ArrivalAirport ?? null;

  const arrivalAirport =
    actualTripLeg?.ArrivalAirport ?? nextTripLeg?.DepartureAirport ?? null;

  const maintenanceActual =
    aircraftBaseInfo.Maintenances.find(
      m =>
        (m.ActualStartDateAndTimeUtcUnix ||
          m.ScheduledStartDateAndTimeUtcUnix) < now &&
        (m.ActualEndDateAndTimeUtcUnix || m.ScheduledEndDateAndTimeUtcUnix) >
          now,
    ) ?? null;
  const maintenanceNext =
    aircraftBaseInfo.Maintenances.find(
      m =>
        (m.ActualStartDateAndTimeUtcUnix ||
          m.ScheduledStartDateAndTimeUtcUnix) > now,
    ) ?? null;
  const maintenancePrev =
    aircraftBaseInfo.Maintenances.find(
      m =>
        (m.ActualEndDateAndTimeUtcUnix || m.ScheduledEndDateAndTimeUtcUnix) <
        now,
    ) ?? null;

  const isOnFlight =
    !!actualTripLeg?.Movement?.IsOffBlocks &&
    !actualTripLeg?.Movement?.IsOnBlocks;

  const airports = aircraftEvents
    .map(e => e.airports)
    .flat()
    .toSorted((a, b) => a.startUnix - b.startUnix);

  const activeAirports = getAircraftLocationInfo(airports);

  const dutyEvents = getCrewDutyEvents(aircraftBaseInfo.CrewDuties);

  const crewAirport = aircraftBaseInfo.CrewDuties.find(
    a => !!a.StartAirport?.ICAO,
  )?.StartAirport;

  const actualEvent = aircraftEvents[0];
  const crewDutyFullActualIndex = aircraftBaseInfo.CrewDuties.findIndex(
    (cd, index, arr) =>
      cd.OnDutyDateTimeUnix < now &&
      cd.OffDutyDateTimeUnix > now &&
      arr[index + 1]?.OnDutyDateTimeUnix === cd.OnDutyDateTimeUnix,
  );
  const crewDutyFullActual: [MaoCrewDuty, MaoCrewDuty] | null =
    crewDutyFullActualIndex >= 0
      ? [
          aircraftBaseInfo.CrewDuties[crewDutyFullActualIndex],
          aircraftBaseInfo.CrewDuties[crewDutyFullActualIndex + 1],
        ]
      : null;
  const crewDutyFullNextIndex = aircraftBaseInfo.CrewDuties.findIndex(
    (cd, index, arr) =>
      cd.OnDutyDateTimeUnix > now &&
      arr[index + 1]?.OnDutyDateTimeUnix === cd.OnDutyDateTimeUnix,
  );
  const crewDutyFullNext: [MaoCrewDuty, MaoCrewDuty] | null =
    crewDutyFullNextIndex >= 0
      ? [
          aircraftBaseInfo.CrewDuties[crewDutyFullNextIndex],
          aircraftBaseInfo.CrewDuties[crewDutyFullNextIndex + 1],
        ]
      : null;

  const airportLastKnown =
    departureAirport ??
    crewAirport ??
    maintenanceActual?.Airport ??
    closestAirport ??
    maintenancePrev?.Airport ??
    maintenanceNext?.Airport ??
    aircraftBaseInfo?.Trip?.Legs.at(-1)?.ArrivalAirport ??
    crewAirport ??
    null;

  // let status = EAircraftStatus.NA;
  const label = actualEvent?.label;
  const organ = actualEvent?.organ;
  const maintenanceType = actualEvent?.maintenanceType;

  // if (actualEvent?.type === EAircraftEvent.Trip) {
  //   status = EAircraftStatus.OnTrip;
  // } else if (actualEvent?.type === EAircraftEvent.Maintenance) {
  //   status = EAircraftStatus.Maintenance;
  // } else if (timeSeverity !== ESeverity.NA) {
  //   status = EAircraftStatus.Available;
  // }
  return {
    ...aircraftBaseInfo,
    id: aircraftBaseInfo.Id,
    actualLabel: label,
    registration: aircraftBaseInfo.Registration,
    tripActual: actualTrip,
    tripLegActual: actualTripLeg,
    actualOrgan: organ ?? null,
    // actualStatus: status,
    // actualTimeSeverity: timeSeverity ?? null,
    dutyEvents,
    dutyRanges,
    actualAircraftEvent,
    activeAirports,
    isOnFlight,
    airports,
    tripNext: nextTrip,
    aircraftEvents,
    airportDeparture: departureAirport,
    airportArrival: arrivalAirport,
    maintenancePrev,
    maintenanceNext,
    maintenanceActual,
    maintenanceType: maintenanceType ?? null,
    crewDutyFullActual,
    crewDutyFullNext,
    airportLastKnown,
  };
};

export const getAircraftInfo = async (aircraftBaseInfo: AircraftBaseData) =>
  cacheWrapper(
    {
      collectionKey: 'getAircraftInfo',
      itemKey: aircraftBaseInfo.Id,
      hashGetter: ({ metaHashes }) => {
        // Add a safety check for Storybook environment
        if (!metaHashes?.dataHash) {
          // For Storybook or test environments, use a static hash or generate one
          return `storybook-${aircraftBaseInfo.Id}-${Date.now()}`;
        }
        return metaHashes.dataHash;
      },
      passThrough: ['Location', 'ActiveTrail'],
    },
    _getAircraftInfo,
  )(aircraftBaseInfo);
